// Gallery pure helpers — no React, no browser APIs. Safe to unit test.

export const FILTER_KEYS = [
  "all", "needs_id", "untagged", "native", "non_native", "beneficial", "weed", "keystone", "endangered",
  "annual", "biennial", "perennial",
];

export const PAGE_SIZE_OPTIONS = [12, 24, 48, 0]; // 0 = All
export const PAGE_SIZE_STORAGE_KEY = "prairie.gallery.pageSize";
export const DEFAULT_PAGE_SIZE = 24;

export const SORT_KEYS = [
  "name_asc", "name_desc",
  "scientific_asc", "scientific_desc",
  "date_new", "date_old",              // upload date (created_at)
  "photo_date_new", "photo_date_old",  // photo date (date_taken || created_at)
  "type",            // native → non-native → weed → needs ID
  "keystone_first",
  "endangered_first",
];
// Secondary sort omits "type / keystone_first / endangered_first" since those
// don't produce meaningful tie-breakers — they group large equivalence classes
// which the primary already handles. Users choose from name/scientific/date
// options as an intra-primary tie-breaker (or "none" for the intrinsic order).
export const SORT_KEYS_SECONDARY = [
  "none",
  "name_asc", "name_desc",
  "scientific_asc", "scientific_desc",
  "date_new", "date_old",
  "photo_date_new", "photo_date_old",
];
export const SORT_STORAGE_KEY = "prairie.gallery.sort";
export const SORT_STORAGE_KEY_SECONDARY = "prairie.gallery.sort2";
export const DEFAULT_SORT = "name_asc";
export const DEFAULT_SORT_SECONDARY = "none";

// Which underlying field a given sort key compares against. The secondary
// dropdown hides every option that shares a field with the current primary
// (so a user can't pick "Newest first" then "Oldest first" — same field,
// opposite directions — a same-field tie-breaker of any direction is also
// redundant since the primary already exhausted that ordering). Keys not
// listed here (e.g. `type`, `keystone_first`) have no sibling to conflict
// with.
export const SORT_FIELD_GROUP = {
  name_asc: "name",
  name_desc: "name",
  scientific_asc: "scientific",
  scientific_desc: "scientific",
  date_new: "date",
  date_old: "date",
  photo_date_new: "photo_date",
  photo_date_old: "photo_date",
};

export const secondarySortOptionsFor = (primaryKey) => {
  const primaryGroup = SORT_FIELD_GROUP[primaryKey];
  return SORT_KEYS_SECONDARY.filter(
    (k) => k === "none" || !primaryGroup || SORT_FIELD_GROUP[k] !== primaryGroup,
  );
};

// Same 10-photo cap the backend enforces via MAX_EXTRA_PHOTOS (see core.py).
export const MAX_EXTRA_PHOTOS_FE = 10;

export const isUnidentified = (item) => {
  const id = item?.identification || {};
  const name = (id.common_name || "").trim().toLowerCase();
  return item?.needs_revisit === true || !name || name === "unknown" || name === "desconocido";
};

// Normalize text for forgiving search: lowercase, strip diacritics,
// and replace any non-alphanumeric (hyphens, punctuation, etc.) with a single space.
// This way "Joe pye weed" matches "Joe-Pye Weed" and "Eutróquio" matches "eutroquio".
export const normalize = (s) =>
  (s || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

// Build a single normalized haystack for an item that covers the original identification,
// any cached translations, and free-form tags — across both common and scientific names.
export const buildSearchHaystack = (item) => {
  const parts = [];
  const id = item?.identification || {};
  parts.push(id.common_name, id.scientific_name);
  const translations = item?.translations || {};
  for (const lang of Object.keys(translations)) {
    parts.push(translations[lang]?.common_name, translations[lang]?.scientific_name);
  }
  if (Array.isArray(item?.tags)) parts.push(...item.tags);
  return normalize(parts.filter(Boolean).join(" "));
};

// Note: `idNative` lives in idCompat and takes an identification object.
// This helper takes the item itself so callers don't repeat the .identification lookup.
export const matchesFilter = (item, id, key, idNative, idEndangered) => {
  if (!id) return false;
  const native = idNative(id);
  switch (key) {
    case "all":        return true;
    case "needs_id":   return isUnidentified(item);
    case "untagged":   return !Array.isArray(item.tags) || item.tags.length === 0;
    case "native":     return native === true;
    case "non_native": return native === false;
    case "beneficial": return (id.plant_type || "").toLowerCase() === "beneficial";
    case "weed":       return (id.plant_type || "").toLowerCase() === "weed";
    case "keystone":   return !!id.is_keystone;
    case "endangered": return !!idEndangered(id);
    case "annual":     return (id.life_cycle || "").toLowerCase() === "annual";
    case "biennial":   return (id.life_cycle || "").toLowerCase() === "biennial";
    case "perennial":  return (id.life_cycle || "").toLowerCase() === "perennial";
    default:           return true;
  }
};

// AND-combined multi-filter: item must match EVERY active key. An empty set of
// keys (or a set containing only "all") is treated as "no filter". Callers pass
// a plain array/Set of filter keys — order doesn't matter, duplicates are fine.
export const matchesFilters = (item, id, keys, idNative, idEndangered) => {
  const arr = Array.isArray(keys) ? keys : Array.from(keys || []);
  const active = arr.filter((k) => k && k !== "all");
  if (active.length === 0) return true;
  for (const k of active) {
    if (!matchesFilter(item, id, k, idNative, idEndangered)) return false;
  }
  return true;
};

// Filter chips that are logically impossible to combine — activating one
// forces the other out of the active set. Symmetric: `EXCLUSIVE_PAIRS[a]`
// contains every chip that CANNOT co-exist with `a`. `needs_id` (Unknown
// plant) conflicts with any category that requires an identification.
export const EXCLUSIVE_PAIRS = {
  native:      ["non_native", "needs_id"],
  non_native:  ["native", "needs_id"],
  beneficial:  ["weed", "needs_id"],
  weed:        ["beneficial", "needs_id"],
  keystone:    ["needs_id"],
  endangered:  ["needs_id"],
  annual:      ["biennial", "perennial", "needs_id"],
  biennial:    ["annual", "perennial", "needs_id"],
  perennial:   ["annual", "biennial", "needs_id"],
  needs_id:    ["native", "non_native", "beneficial", "weed", "keystone", "endangered", "annual", "biennial", "perennial"],
};
